import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { Link, generatePath, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { surveysApi, type SurveyReport } from '@/api/surveys';
import { Copy, Eye, Share } from '@/components/icons';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import {
    EmptyState,
    FilterChipBar,
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableHeaderRow,
    DataTableRow,
    ListSection,
    SearchField,
} from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { buildWhatsAppUrl } from '@/lib/whatsapp';
import { PatientDetailPage, SurveyResponsePage } from '@/pages/constants';
import { getSharableSurveyText } from '../utils/getSurveyText';

const SURVEY_STATUS_FILTERS = [
    { value: 'all', label: 'Todos' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'COMPLETED', label: 'Concluído' },
];

export default function SentSurveysTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const backTo = `${location.pathname}${location.search}`;
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const { data: reports = [], isLoading } = useQuery({
        queryKey: ['survey-reports'],
        queryFn: surveysApi.listReports,
    });

    const statusCounts = {
        all: reports.length,
        PENDING: reports.filter((report) => report.status === 'PENDING').length,
        COMPLETED: reports.filter((report) => report.status === 'COMPLETED').length,
    } as Record<string, number>;

    const filteredReports = reports.filter((report) => {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
            report.patientName.toLowerCase().includes(search) ||
            report.surveyName.toLowerCase().includes(search);

        const matchesStatus = statusFilter === 'all' || report.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const getStatusBadge = (status: string) => {
        const statusMap: Record<
            string,
            {
                label: string;
                variant: 'warning' | 'success' | 'neutral';
            }
        > = {
            PENDING: {
                label: 'Pendente',
                variant: 'warning',
            },
            COMPLETED: {
                label: 'Concluído',
                variant: 'success',
            },
        };

        const config = statusMap[status] || {
            label: status,
            variant: 'neutral' as const,
        };

        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const getPatientAvatarUrl = (report: SurveyReport) => report.avatarUrl;

    const handleShareAction = async (report: SurveyReport, action: 'copy' | 'whatsapp') => {
        const toastId = toast.loading('Gerando link de acesso...');

        try {
            const response = await surveysApi.getAssignmentToken(report.patientId, report.id);
            const data = response?.data || response;
            const token = data?.unlock_token || data?.token;

            if (!token) {
                toast.dismiss(toastId);
                toast.error('Não foi possível recuperar o token de acesso.');
                return;
            }

            const link = `${window.location.origin}/patients/survey/answer?token=${token}`;

            if (action === 'copy') {
                await navigator.clipboard.writeText(link);
                toast.dismiss(toastId);
                toast.success('Link copiado para a área de transferência!');
                return;
            }

            const expiryDate = data?.unlock_token_expires_at
                ? new Date(data.unlock_token_expires_at)
                : null;

            let text = getSharableSurveyText(link);

            if (expiryDate) {
                const formattedDate = new Intl.DateTimeFormat('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }).format(expiryDate);
                text += `\nVálido até: ${formattedDate}`;
            }

            window.open(buildWhatsAppUrl(text), '_blank');
            toast.dismiss(toastId);
            toast.success('WhatsApp aberto!');
        } catch (error) {
            console.error(error);
            toast.dismiss(toastId);
            toast.error('Erro ao buscar o token de acesso.');
        }
    };

    const formatSentAt = (sentAt: string) =>
        format(new Date(sentAt), "dd/MM/yyyy 'às' HH:mm", {
            locale: ptBR,
        });

    const handleViewResponses = (report: SurveyReport) => {
        navigate(
            `/${generatePath(SurveyResponsePage, {
                reportId: report.id,
            })}`,
            {
                state: {
                    report,
                },
            },
        );
    };

    const renderReportAction = (report: SurveyReport, compact = false) => {
        if (report.status !== 'COMPLETED') {
            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant={compact ? 'surface' : 'ghost'}
                            size="sm"
                            className={
                                compact
                                    ? 'w-full justify-center'
                                    : 'text-muted-foreground hover:bg-brand-subtle hover:text-brand'
                            }
                        >
                            <Share />
                            Compartilhar
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={() => handleShareAction(report, 'copy')}
                            className="cursor-pointer"
                        >
                            <Copy className="mr-2 size-4" />
                            Copiar link
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => handleShareAction(report, 'whatsapp')}
                            className="cursor-pointer"
                        >
                            <WhatsAppIcon className="mr-2 size-4" />
                            Compartilhar no WhatsApp
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        }

        return (
            <Button
                variant="surface"
                size="sm"
                onClick={() => handleViewResponses(report)}
                title="Visualizar respostas"
                className={compact ? 'w-full justify-center' : undefined}
            >
                <Eye />
                {compact ? 'Ver respostas' : 'Visualizar respostas'}
            </Button>
        );
    };

    return (
        <ListSection
            title="Histórico de Atribuições"
            contentClassName="p-0"
            headerClassName="p-4 sm:p-6"
            search={
                <SearchField
                    className="md:w-72"
                    placeholder="Buscar por paciente ou formulário..."
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                />
            }
            filters={
                <FilterChipBar
                    ariaLabel="Filtrar atribuições por status"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    className="w-full flex-nowrap gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0"
                    options={SURVEY_STATUS_FILTERS.map((status) => ({
                        ...status,
                        count: statusCounts[status.value] || 0,
                    }))}
                />
            }
        >
            <div className="md:hidden">
                {isLoading ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                        Carregando...
                    </div>
                ) : filteredReports.length === 0 ? (
                    <div className="px-4 py-6">
                        <EmptyState title="Nenhuma atribuição encontrada." />
                    </div>
                ) : (
                    <div className="grid gap-3 p-4">
                        {filteredReports.map((report) => (
                            <div
                                key={report.id}
                                className="rounded-2xl border border-border/80 bg-card/95 p-4 shadow-sm"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex min-w-0 items-start gap-3">
                                        <PatientAvatar
                                            name={report.patientName}
                                            avatarUrl={getPatientAvatarUrl(report)}
                                            className="size-10"
                                            fallbackClassName="text-sm"
                                        />
                                        <div className="min-w-0">
                                            {report.patientId ? (
                                                <Link
                                                    to={`/${generatePath(PatientDetailPage, {
                                                        patientId: report.patientId,
                                                    })}`}
                                                    state={{ backTo }}
                                                    className="block max-w-full"
                                                >
                                                    <h3 className="truncate text-sm font-semibold text-foreground">
                                                        {report.patientName}
                                                    </h3>
                                                </Link>
                                            ) : (
                                                <h3 className="truncate text-sm font-semibold text-foreground">
                                                    {report.patientName}
                                                </h3>
                                            )}
                                            <p className="mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground">
                                                {report.surveyName}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="shrink-0">{getStatusBadge(report.status)}</div>
                                </div>

                                <p className="mt-3 text-xs text-muted-foreground">
                                    Criado em {formatSentAt(report.sentAt)}
                                </p>

                                <div className="mt-4">{renderReportAction(report, true)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="hidden md:block">
                <DataTable>
                    <DataTableHeader className="border-b border-border">
                        <DataTableHeaderRow>
                            <DataTableHead>Paciente</DataTableHead>
                            <DataTableHead>Formulário</DataTableHead>
                            <DataTableHead>Status</DataTableHead>
                            <DataTableHead>Criado em</DataTableHead>
                            <DataTableHead>Ações</DataTableHead>
                        </DataTableHeaderRow>
                    </DataTableHeader>

                    <DataTableBody>
                        {isLoading ? (
                            <DataTableRow>
                                <DataTableCell colSpan={5} variant="empty">
                                    Carregando...
                                </DataTableCell>
                            </DataTableRow>
                        ) : filteredReports.length === 0 ? (
                            <DataTableRow>
                                <DataTableCell colSpan={5} variant="empty">
                                    <EmptyState title="Nenhuma atribuição encontrada." />
                                </DataTableCell>
                            </DataTableRow>
                        ) : (
                            filteredReports.map((report) => (
                                <DataTableRow key={report.id}>
                                    <DataTableCell>
                                        <div className="group flex items-center gap-3">
                                            <PatientAvatar
                                                name={report.patientName}
                                                avatarUrl={getPatientAvatarUrl(report)}
                                                className="size-10"
                                                fallbackClassName="text-sm"
                                            />
                                            <div className="min-w-0">
                                                {report.patientId ? (
                                                    <Link
                                                        to={`/${generatePath(PatientDetailPage, {
                                                            patientId: report.patientId,
                                                        })}`}
                                                        state={{ backTo }}
                                                        className="inline-flex max-w-full"
                                                    >
                                                        <h3 className="truncate font-semibold text-foreground transition-colors group-hover:text-brand">
                                                            {report.patientName}
                                                        </h3>
                                                    </Link>
                                                ) : (
                                                    <h3 className="truncate font-semibold text-foreground">
                                                        {report.patientName}
                                                    </h3>
                                                )}
                                            </div>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell variant="subtle">
                                        {report.surveyName}
                                    </DataTableCell>

                                    <DataTableCell>{getStatusBadge(report.status)}</DataTableCell>

                                    <DataTableCell variant="subtle">
                                        {formatSentAt(report.sentAt)}
                                    </DataTableCell>

                                    <DataTableCell>{renderReportAction(report)}</DataTableCell>
                                </DataTableRow>
                            ))
                        )}
                    </DataTableBody>
                </DataTable>
            </div>
        </ListSection>
    );
}
